/**
 * vx-stories.js
 *
 * Feed : init Swiper par instance (.vx-swiper).
 *   - boucle infinie si data-loop="true" (>= 3 vidéos, posé par Liquid)
 *   - slidesPerView calculé depuis data-slides-desktop / data-slides-mobile (valeur en %)
 *   - autoplay intersection-based (preview 5s, pause hors viewport)
 *   - pas de flèches
 *
 * Modal : lecteur plein écran inchangé.
 */

(() => {
  const q  = (s, r = document) => r.querySelector(s);
  const qa = (s, r = document) => Array.from(r.querySelectorAll(s));

  const FEED_PREVIEW_DURATION = 5; // secondes de preview dans le feed

  /* ── Détection mobile ─────────────────────────────────────── */
  const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

  /* ============================
     FEED — Swiper init
  ============================ */

  function initAllSwipers() {
    qa('.vx-swiper').forEach(el => {
      if (el.dataset.vxInit === '1') return;
      el.dataset.vxInit = '1';
      initSwiper(el);
    });
  }

  function initSwiper(el) {
    const loop         = el.dataset.loop === 'true';
    const spaceBetween = parseInt(el.dataset.spaceBetween || '12', 10);

    /*
     * slidesPerView calculé à partir du pourcentage stocké en data-slides-desktop/mobile.
     * On convertit "85" (%) → nombre de slides visibles.
     * Ex : 85% de largeur visible = environ 1.18 slides → on utilise 'auto'
     * et on laisse Swiper gérer via la largeur CSS du slide.
     *
     * Stratégie : on utilise slidesPerView: 'auto' + width en % sur .swiper-slide via CSS inline.
     * C'est la façon la plus flexible et la plus robuste avec Swiper.
     */
    const pctDesktop = parseInt(el.dataset.slidesDesktop || '85', 10);
    const pctMobile  = parseInt(el.dataset.slidesMobile  || '85', 10);

    // Appliquer la largeur % sur chaque slide réel (pas les clones Swiper)
    function applySlideWidths() {
      const pct = isMobile() ? pctMobile : pctDesktop;
      qa('.swiper-slide:not(.swiper-slide-duplicate)', el).forEach(slide => {
        slide.style.width = pct + '%';
      });
    }
    applySlideWidths();

    const swiper = new Swiper(el, {
      slidesPerView:  'auto',
      spaceBetween,
      loop,
      grabCursor:     true,
      /*
       * centeredSlides: false → le slide actif s'aligne à gauche, comportement naturel.
       * Mettre à true si on veut centrer le slide actif (style story plein écran).
       */
      centeredSlides: false,
      /*
       * Pas de navigation (flèches supprimées).
       * Pas de pagination (non demandée).
       */
      on: {
        slideChange() {
          // Pause toutes les vidéos, joue celle du slide actif
          updateFeedVideoPlayback(el, this.realIndex);
        },
        resize() {
          applySlideWidths();
        },
      },
    });

    // Init autoplay intersection
    initFeedAutoplay(el);
    setTimeout(() => updateFeedVideoPlayback(el, swiper.realIndex), 50);

    // Première lecture
    updateFeedVideoPlayback(el, swiper.realIndex);
  }

  /* ── Lecture vidéo du slide actif ────────────────────────── */
  function updateFeedVideoPlayback(swiperEl, realIndex) {
    // On cible uniquement les slides non-clones (Swiper duplique les slides en mode loop)
    qa('.swiper-slide:not(.swiper-slide-duplicate) .vx-story-vid video', swiperEl)
      .forEach((v, i) => {
        if (i === realIndex) {
          // v.pause(); // stop d'abord pour reset propre
          v.currentTime = 0;
          v.load();  // force reload si la vidéo était bloquée
          v.play().catch(() => {});
        } else {
          // v.pause();
          // v.currentTime = 0; // reset les autres aussi pour économiser les ressources
        }
      });
  }

  /* ── Autoplay intersection ───────────────────────────────── */
  function initFeedAutoplay(scope) {
    const videos = qa('.vx-story-vid video', scope);
    if (!videos.length) return;

    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        const v = e.target;
        // Ne pas autoplay les slides dupliqués de Swiper (clones loop)
        if (v.closest('.swiper-slide-duplicate')) return;
        if (!e.isIntersecting) {
          v.pause();
        }
      }),
      { threshold: 0.5 }
    );

    videos.forEach(v => {
      v.preload     = 'metadata';
      v.muted       = true;
      v.playsInline = true;
      Object.assign(v.style, { willChange: 'transform,opacity', transition: 'opacity 220ms linear', opacity: '1' });

      v.addEventListener('timeupdate', () => {
        if (v.currentTime >= FEED_PREVIEW_DURATION) {
          v.currentTime = 0;
          v.play().catch(() => {});
        }
      });

      io.observe(v);
    });
  }

  /* ============================
     CONTRÔLES MODAL (injectés par slide)
  ============================ */

  function injectCSS() {
    if (document.getElementById('vx-ctrl-css')) return;
    const s = document.createElement('style');
    s.id = 'vx-ctrl-css';
    s.textContent = `
      .vx-ctrl-overlay{position:absolute;inset:0;z-index:20;pointer-events:none}
      .vx-ctrl-center{
        position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
        display:flex;align-items:center;gap:40px;
        opacity:0;transition:opacity 240ms ease;pointer-events:none
      }
      .vx-ctrl-center.is-visible{opacity:1;pointer-events:auto}
      .vx-ctrl-btn{
        width:40px;height:40px;border-radius:50%;border:none;
        background:rgba(255,255,255,.13);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);
        color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;
        flex-direction:column;gap:3px;padding:0;outline:none;
        transition:background 160ms ease,transform 120ms ease
      }
      .vx-ctrl-btn:active{transform:scale(.88);background:rgba(255,255,255,.22)}
      .vx-ctrl-btn--play{width:64px;height:64px}
      .vx-ctrl-btn svg{display:block}
      .vx-ctrl-btn__label{font-size:8px;font-weight:700;color:#fff;line-height:1;letter-spacing:.03em}

      .vx-slide-loader{
        position:absolute;inset:0;z-index:30;
        display:flex;align-items:center;justify-content:center;
        background:rgba(0,0,0,.45);opacity:0;pointer-events:none;
        transition:opacity 200ms ease;
      }
      .vx-slide-loader.is-loading{opacity:1}
      .vx-slide-loader__spinner{
        width:42px;height:42px;border-radius:50%;
        border:3px solid rgba(255,255,255,.25);border-top-color:#fff;
        animation:vx-spin .75s linear infinite
      }
      @keyframes vx-spin{to{transform:rotate(360deg)}}

      .vx-seekbar{
        position:absolute;bottom:0;left:0;right:0;z-index:25;
        padding:18px 14px 14px;
        background:linear-gradient(transparent,rgba(0,0,0,.55));
        pointer-events:auto;cursor:pointer;touch-action:none;
        user-select:none;-webkit-user-select:none;
      }
      .vx-seekbar__track{position:relative;height:4px;border-radius:2px;background:rgba(255,255,255,.28);transition:height 160ms ease;}
      .vx-seekbar:hover .vx-seekbar__track,.vx-seekbar.is-dragging .vx-seekbar__track{height:6px;}
      .vx-seekbar__fill{position:absolute;inset:0;right:auto;background:#fff;border-radius:2px;pointer-events:none;width:0%;}
      .vx-seekbar__thumb{
        position:absolute;top:50%;width:14px;height:14px;border-radius:50%;background:#fff;
        transform:translate(-50%,-50%) scale(0);transition:transform 160ms ease;
        pointer-events:none;box-shadow:0 1px 4px rgba(0,0,0,.4);
      }
      .vx-seekbar:hover .vx-seekbar__thumb,.vx-seekbar.is-dragging .vx-seekbar__thumb{transform:translate(-50%,-50%) scale(1);}
      .vx-seekbar__time{display:flex;justify-content:space-between;margin-top:7px;font-size:11px;font-weight:600;color:rgba(255,255,255,.8);letter-spacing:.04em;pointer-events:none;font-family:monospace;}

      @media(max-width:768px){
        .vx-modal__rail{display:flex!important;flex-direction:row!important;width:calc(100% * var(--vx-story-count,1))!important;height:100%!important;}
        .vx-modal__slide{width:calc(100% / var(--vx-story-count,1))!important;height:100%!important;flex-shrink:0!important;padding:0!important;}
      }
    `;
    document.head.appendChild(s);
  }

  const SVG = {
    play:    `<svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>`,
    pause:   `<svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>`,
    rewind:  `<svg width="19" height="19" viewBox="0 0 24 24" fill="#fff"><path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>`,
    forward: `<svg width="19" height="19" viewBox="0 0 24 24" fill="#fff"><path d="M12.01 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/></svg>`,
  };

  const ctrlTimers = new WeakMap();
  const mk = (tag, cls, html = '') => { const el = document.createElement(tag); if (cls) el.className = cls; if (html) el.innerHTML = html; return el; };
  const fmtTime = sec => { if (!isFinite(sec) || isNaN(sec)) return '0:00'; const m = Math.floor(sec/60), s = Math.floor(sec%60); return `${m}:${s.toString().padStart(2,'0')}`; };
  const showLoader = slide => q('.vx-slide-loader', slide)?.classList.add('is-loading');
  const hideLoader = slide => q('.vx-slide-loader', slide)?.classList.remove('is-loading');

  function createSeekbar(slide) {
    const v = q('video', slide);
    const inner = q('.vx-modal__slideInner', slide) || slide;
    const bar = mk('div', 'vx-seekbar');
    bar.innerHTML = `<div class="vx-seekbar__track"><div class="vx-seekbar__fill"></div><div class="vx-seekbar__thumb"></div></div><div class="vx-seekbar__time"><span class="vx-seekbar__cur">0:00</span><span class="vx-seekbar__dur">0:00</span></div>`;
    inner.appendChild(bar);
    const fill = q('.vx-seekbar__fill', bar), thumb = q('.vx-seekbar__thumb', bar);
    const cur  = q('.vx-seekbar__cur',  bar), dur   = q('.vx-seekbar__dur',   bar);

    const updateBar = () => { if (!v?.duration) return; const p = (v.currentTime/v.duration)*100; fill.style.width = p+'%'; thumb.style.left = p+'%'; cur.textContent = fmtTime(v.currentTime); dur.textContent = fmtTime(v.duration); };
    if (v) { v.addEventListener('timeupdate', updateBar); v.addEventListener('loadedmetadata', () => { dur.textContent = fmtTime(v.duration); }); }

    let dragging = false, wasPaused = false;
    const seekFromX = x => { if (!v?.duration) return; const r = bar.getBoundingClientRect(); v.currentTime = Math.min(1, Math.max(0, (x-r.left)/r.width)) * v.duration; updateBar(); };
    const startDrag = x => { dragging=true; bar.classList.add('is-dragging'); if(v){wasPaused=v.paused; if(!v.paused)v.pause();} seekFromX(x); };
    const moveDrag  = x => { if(dragging) seekFromX(x); };
    const endDrag   = ()=> { if(!dragging)return; dragging=false; bar.classList.remove('is-dragging'); if(v&&!wasPaused)v.play().catch(()=>{}); };

    bar.addEventListener('mousedown',  e => { e.stopPropagation(); startDrag(e.clientX); });
    document.addEventListener('mousemove', e => { if(dragging){e.stopPropagation(); moveDrag(e.clientX);} });
    document.addEventListener('mouseup',   ()=> { if(dragging) endDrag(); });
    bar.addEventListener('touchstart', e => { e.stopPropagation(); startDrag(e.touches[0].clientX); }, {passive:true});
    bar.addEventListener('touchmove',  e => { e.stopPropagation(); moveDrag(e.touches[0].clientX); },  {passive:true});
    bar.addEventListener('touchend',   ()=> endDrag(), {passive:true});
  }

  function createControls(slide) {
    if (q('.vx-ctrl-overlay', slide)) return;
    const inner = q('.vx-modal__slideInner', slide) || slide;
    inner.style.position = 'relative';
    const loader = mk('div', 'vx-slide-loader'); loader.innerHTML = `<div class="vx-slide-loader__spinner"></div>`; inner.appendChild(loader);
    const overlay = mk('div', 'vx-ctrl-overlay');
    const center  = mk('div', 'vx-ctrl-center');
    const btnBack = mk('button', 'vx-ctrl-btn', SVG.rewind  + `<span class="vx-ctrl-btn__label">3s</span>`);
    const btnPlay = mk('button', 'vx-ctrl-btn vx-ctrl-btn--play', SVG.pause);
    const btnFwd  = mk('button', 'vx-ctrl-btn', SVG.forward + `<span class="vx-ctrl-btn__label">3s</span>`);
    btnBack.setAttribute('aria-label', '-3s'); btnPlay.setAttribute('aria-label', 'Play/Pause'); btnFwd.setAttribute('aria-label', '+3s');
    center.append(btnPlay); overlay.append(center); inner.appendChild(overlay);
    createSeekbar(slide); 
    const v = q('video', slide);
    const syncIcon = () => { btnPlay.innerHTML = (v&&!v.paused) ? SVG.pause : SVG.play; };
    if (v) ['play','pause','ended'].forEach(ev => v.addEventListener(ev, syncIcon));
    if (v) { v.addEventListener('waiting', ()=>showLoader(slide)); v.addEventListener('canplay', ()=>hideLoader(slide)); v.addEventListener('playing', ()=>hideLoader(slide)); v.addEventListener('stalled', ()=>showLoader(slide)); }
    btnBack.addEventListener('click', e => { e.stopPropagation(); if(v) v.currentTime=Math.max(0,v.currentTime-3); showControls(slide,true); });
    btnFwd.addEventListener( 'click', e => { e.stopPropagation(); if(v) v.currentTime=Math.min(v.duration||0,v.currentTime+3); showControls(slide,true); });
    btnPlay.addEventListener('click', e => { e.stopPropagation(); if(!v)return; v.paused?v.play().catch(()=>{}):v.pause(); showControls(slide,!v.paused); });
    if (v) v.addEventListener('ended', ()=>hideControls(slide));
  }

  function showControls(slide, withTimer) {
    const center = q('.vx-ctrl-center', slide); if(!center)return;
    center.classList.add('is-visible');
    if (ctrlTimers.has(slide)) { clearTimeout(ctrlTimers.get(slide)); ctrlTimers.delete(slide); }
    if (withTimer) {
      const v = q('video', slide);
      ctrlTimers.set(slide, setTimeout(()=>{ if(v&&v.paused)return; center.classList.remove('is-visible'); ctrlTimers.delete(slide); }, 3000));
    }
  }

  function hideControls(slide) {
    const center = q('.vx-ctrl-center', slide); if(!center)return;
    if (ctrlTimers.has(slide)) { clearTimeout(ctrlTimers.get(slide)); ctrlTimers.delete(slide); }
    center.classList.remove('is-visible');
  }

  /* ============================
     MODAL
  ============================ */
  const state = { stories:[], index:0, muted:false, modal:null, rail:null, bar:null, cta:null, soundBtn:null, navUp:null, navDown:null, rafId:0, wheelLock:false, sourcesLoaded:false };

  function getStoryList(scope) {
    return qa('.vx-story-vid', scope || document)
      .filter(wrap => !wrap.closest('.swiper-slide-duplicate')) // exclure les clones Swiper
      .map(wrap => {
        const btn = q('.vx-story-open', wrap), v = q('video', wrap);
        const src = btn?.dataset.video || q('source',v)?.getAttribute('src') || v?.currentSrc || '';
        return { wrap, src, url: btn?.dataset.url || '#' };
      }).filter(x => x.src);
  }

  function cacheModalEls() {
    state.modal = q('.vx-modal'); if(!state.modal) return false;
    state.rail     = q('.vx-modal__rail',                  state.modal);
    state.cta      = q('.vx-modal__cta',                   state.modal);
    state.soundBtn = q('.vx-modal__sound',                 state.modal);
    state.navUp    = q('.vx-modal__navbtn[data-dir="-1"]', state.modal);
    state.navDown  = q('.vx-modal__navbtn[data-dir="1"]',  state.modal);
    return true;
  }

  function buildRail() {
    state.rail.innerHTML = '';
    state.stories.forEach((_,i) => {
      const slide = mk('div', 'vx-modal__slide'); slide.dataset.index = i;
      slide.innerHTML = `<div class="vx-modal__slideInner"><video playsinline preload="auto"></video></div>`;
      state.rail.appendChild(slide);
    });
    state.rail.style.setProperty('--vx-story-count', state.stories.length);
  }

  function loadAllVideoSourcesOnce() {
    qa('.vx-modal__slide', state.modal).forEach(slide => {
      const v = q('video', slide), item = state.stories[+slide.dataset.index];
      if (!v||!item) return;
      v.loop=false; v.controls=false; v.playsInline=true; v.muted=true;
      if (!v.src) { v.src=item.src; v.preload='auto'; v.load(); }
      Object.assign(v.style, {transition:'opacity 180ms linear', opacity:'1'});
      createControls(slide);
    });
    state.sourcesLoaded = true;
  }

  const getActiveSlide = () => q(`.vx-modal__slide[data-index="${state.index}"]`, state.modal);
  const getActiveVideo = () => { const s=getActiveSlide(); return s ? q('video',s) : null; };

  function applyMuteToAll(muted) { state.muted=muted; qa('.vx-modal__slide video',state.modal).forEach(v=>v.muted=muted); state.soundBtn?.classList.toggle('is-muted',muted); }

  function updateCTAFromStory() {
    if(!state.cta)return;
    const wrap=state.stories[state.index]?.wrap, card=wrap?.querySelector('.vx-story-card'); if(!card)return;
    const get=s=>card.querySelector(s);
    const ctaImg=q('.vx-modal__ctaImg',state.cta), ctaTitle=q('.vx-modal__ctaTitle',state.cta), ctaPrice=q('.vx-modal__ctaPrice',state.cta);
    if(ctaTitle) ctaTitle.textContent=get('.vx-story-card__title')?.textContent.trim()??'';
    if(ctaPrice) ctaPrice.innerHTML=get('.vx-story-card__price')?.innerHTML??'';
    if(ctaImg){const imgEl=get('.vx-story-card__thumb img'), src=imgEl?.getAttribute('src')??''; src?Object.assign(ctaImg,{src,alt:imgEl?.getAttribute('alt')??''},{style:'opacity:1'}):(ctaImg.removeAttribute('src'),ctaImg.style.opacity='0');}
  }

  function setActiveSlide() {
    if (isMobile()) {
      const w = state.rail.offsetWidth / state.stories.length;
      state.rail.style.transition = 'transform 320ms cubic-bezier(.4,0,.2,1)';
      state.rail.style.transform = `translate3d(${-state.index * w}px,0,0)`;
    } else {
      state.rail.style.transition = 'transform 260ms cubic-bezier(.2,.8,.2,1)';
      state.rail.style.transform = `translate3d(0,${-state.index * 100}%,0)`;
    }

    qa('.vx-modal__slide', state.modal).forEach(s => {
      s.removeAttribute('data-active');
      hideControls(s);
    });

    const slide = getActiveSlide();
    if (slide) slide.setAttribute('data-active', '1');

    state.cta.href = state.stories[state.index]?.url ?? '#';
    updateCTAFromStory();

    const v = getActiveVideo();
    if (!v) return;

    // Pause + reset toutes les autres vidéos
    qa('.vx-modal__slide video', state.modal).forEach(x => {
      if (x !== v) { x.pause(); x.currentTime = 0; }
    });

    v.muted = state.muted;
    v.currentTime = 0;

    // Forcer le load si src pas encore chargée
    if (!v.src || v.readyState === 0) {
      const item = state.stories[state.index];
      if (item) { v.src = item.src; v.load(); }
    }

    if (v.readyState >= 2) {
      hideLoader(slide);
      v.play().catch(() => {});
    } else {
      showLoader(slide);
      const onReady = () => {
        hideLoader(slide);
        if (getActiveSlide() === slide) v.play().catch(() => {});
      };
      v.addEventListener('canplay',    onReady, { once: true });
      v.addEventListener('loadeddata', onReady, { once: true });
    }

    v.onended = () => state.index >= state.stories.length - 1
      ? (state.index = 0, setActiveSlide())
      : goTo(1);

    if (state.navUp)   state.navUp.disabled   = state.index <= 0;
    if (state.navDown) state.navDown.disabled = state.index >= state.stories.length - 1;
  }

  function openModalFor(btn) {
    if (!cacheModalEls()) return;
    const scope = btn.closest('.vx-stories') || document;
    state.stories = getStoryList(scope);
    if (!state.stories.length) return;
    const wrap = btn.closest('.vx-story-vid');
    state.index = Math.max(0, state.stories.findIndex(x => x.wrap === wrap));
    state.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    buildRail();
    loadAllVideoSourcesOnce();
    applyMuteToAll(false);
    try { wrap?.querySelector('video')?.pause(); } catch(_) {}
    const isiOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isiOS) {
      const activeSlideEl = q(`.vx-modal__slide[data-index="${state.index}"]`, state.modal);
      const vios = activeSlideEl ? q('video', activeSlideEl) : null;
      if (vios) { vios.muted = false; vios.currentTime = 0; vios.play().catch(() => {}); }
    }
    setActiveSlide();
    startProgressLoop();
    setTimeout(() => {
      const v = getActiveVideo();
      if (v && v.paused) v.play().catch(() => {});
    }, 80);
  }

  function closeModal() {
    if(!state.modal)return; stopProgressLoop();
    qa('.vx-modal__slide video',state.modal).forEach(v=>v.pause());
    state.modal.hidden=true; document.body.style.overflow='';
    try{state.stories?.[state.index]?.wrap?.querySelector('video')?.play().catch(()=>{});}catch(_){}
  }

  function goTo(dir) { const next=state.index+dir; if(next<0||next>=state.stories.length)return; state.index=next; setActiveSlide(); }

  function startProgressLoop() {
    stopProgressLoop();
    const tick=()=>{ if(!state.modal||state.modal.hidden)return; const v=getActiveVideo(); if(state.bar){state.bar.style.width=v?.duration?(v.currentTime/v.duration*100)+'%':'0%';} state.rafId=requestAnimationFrame(tick); };
    state.rafId=requestAnimationFrame(tick);
  }
  function stopProgressLoop(){if(state.rafId){cancelAnimationFrame(state.rafId);state.rafId=0;}}

  const tap={t:0,x:0};
  function doSeek(clientX,slide){const rect=slide.getBoundingClientRect(),left=(clientX-rect.left)<rect.width/2,v=q('video',slide);if(v)v.currentTime=left?Math.max(0,v.currentTime-3):Math.min(v.duration||0,v.currentTime+3);showControls(slide,true);}

  document.addEventListener('click', e=>{
    const open=e.target.closest('.vx-story-open');
    if(open&&!open.closest('.swiper-slide-duplicate')){e.preventDefault();openModalFor(open);return;}
    if(!state.modal||state.modal.hidden)return;
    if(e.target.closest('.vx-modal__close,.vx-modal__backdrop')){closeModal();return;}
    const soundBtn=e.target.closest('.vx-modal__sound'); if(soundBtn){applyMuteToAll(!state.muted);getActiveVideo()?.play().catch(()=>{});return;}
    const navBtn=e.target.closest('.vx-modal__navbtn'); if(navBtn){goTo(+navBtn.dataset.dir);return;}
  });

  document.addEventListener('pointerdown', e=>{
    if(!state.modal||state.modal.hidden)return;
    if(e.target.closest('.vx-ctrl-btn,.vx-seekbar,.vx-modal__close,.vx-modal__backdrop,.vx-modal__sound,.vx-modal__navbtn,.vx-story-open'))return;
    const slide=e.target.closest('.vx-modal__slide'); if(!slide)return;
    const now=Date.now(), dx=Math.abs(e.clientX-tap.x);
    if(now-tap.t<280&&dx<50){tap.t=0;doSeek(e.clientX,slide);}
    else{tap.t=now;tap.x=e.clientX;const v=q('video',slide);if(!v)return;const center=q('.vx-ctrl-center',slide);if(!center?.classList.contains('is-visible'))showControls(slide,true);}
  });

  document.addEventListener('wheel', e=>{
    if(!state.modal||state.modal.hidden||state.wheelLock)return;
    state.wheelLock=true; setTimeout(()=>state.wheelLock=false,300);
    if(e.deltaY>20)goTo(1); else if(e.deltaY<-20)goTo(-1);
  },{passive:true});

  const touch={y:0,x:0,moved:false};
  document.addEventListener('touchstart',e=>{if(!state.modal||state.modal.hidden||e.target.closest('.vx-seekbar'))return;touch.y=e.touches[0].clientY;touch.x=e.touches[0].clientX;touch.moved=false;},{passive:true});
  document.addEventListener('touchmove', e=>{if(!state.modal||state.modal.hidden||e.target.closest('.vx-seekbar'))return;if(Math.abs(e.touches[0].clientY-touch.y)>10||Math.abs(e.touches[0].clientX-touch.x)>10)touch.moved=true;},{passive:true});
  document.addEventListener('touchend',  e=>{
    if(!state.modal||state.modal.hidden||e.target.closest('.vx-seekbar'))return;
    const t=e.changedTouches[0],diffY=t.clientY-touch.y,diffX=t.clientX-touch.x;
    if(touch.moved){
      if(isMobile()){if(Math.abs(diffX)>60&&Math.abs(diffX)>Math.abs(diffY)){goTo(diffX<0?1:-1);return;}}
      else          {if(Math.abs(diffY)>60&&Math.abs(diffY)>Math.abs(diffX)){goTo(diffY<0?1:-1);return;}}
    }
  },{passive:true});

  document.addEventListener('keydown', e=>{
    if(!state.modal||state.modal.hidden)return;
    const slide=getActiveSlide(),v=getActiveVideo();
    if(e.key==='Escape'){closeModal();return;} if(e.key==='ArrowDown'){goTo(1);return;} if(e.key==='ArrowUp'){goTo(-1);return;}
    if(e.key===' '||e.key==='k'){e.preventDefault();if(!v)return;v.paused?v.play().catch(()=>{}):v.pause();if(slide)showControls(slide,!v.paused);}
    if((e.key==='ArrowLeft'||e.key==='ArrowRight')&&v){e.preventDefault();const l=e.key==='ArrowLeft';v.currentTime=l?Math.max(0,v.currentTime-3):Math.min(v.duration||0,v.currentTime+3);if(slide)showControls(slide,true);}
  });

  document.addEventListener('fullscreenchange',()=>{const fs=!!(document.fullscreenElement||document.webkitFullscreenElement);if(state.modal)qa('.vx-modal__slide video',state.modal).forEach(v=>v.controls=fs);});

  /* ── Init ─────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => { injectCSS(); initAllSwipers(); cacheModalEls(); });
  if (document.readyState !== 'loading') { injectCSS(); initAllSwipers(); }
})();